/**
 * liveTurnAdmissionRegistry — the send gate's synchronous view of the OPEN
 * thread's LIVE TURNS (§11 WS-3 of docs/PLAN-mid-turn-fold-in-20260915.md;
 * ruling T-R14, Miles 2026-09-20: in a shared room every message sent while
 * Prest0n works is EVIDENCE the primary dispositions — it attaches to the
 * running work with its sender named, never queues behind it, never becomes an
 * unrelated turn).
 *
 * Why a module registry: `useGlobalChat` sits UPSTREAM of the thread store that
 * listens to `group_thread_messages/{t}/live` (GlobalChatSystem.tsx mounts the
 * hook first and the store after; the only inbound channel is the config
 * object), so the send gate cannot read the store's `activeThreadLiveTurns`
 * directly. The host publishes the open thread's live docs here in one effect;
 * the gate reads the attach target synchronously at send time — the
 * liveSendRegistry idiom (a singleton, immune to render batching, with a TTL so
 * an entry orphaned by an unmounted host self-expires instead of pointing a
 * send at a turn nobody is watching any more).
 *
 * What a target is: the newest still-moving doc on the thread whose server
 * lever admitted participants (`noteAdmission === 'participants'` — the server
 * re-checks every attach through the participant door; this field is only the
 * hint that a post is worth making), whose asker is SOMEONE ELSE, on a thread
 * where this tab drives no turn of its own (the own path — the interjection
 * bound to this tab's task — handles that). Null means "today's path".
 *
 * Pure: no React, no Firestore, no firebase import.
 */
import { LIVE_TURN_STALE_MS, type LiveTurnView } from '../../services/groupThreads/liveTurns';
import { isLiveSendInFlight } from './liveSendRegistry';

/** A published list older than this is an orphan (the host unmounted without clearing). */
export const LIVE_TURN_ADMISSION_TTL_MS = 15 * 60 * 1000;

export interface ParticipantAttachTarget {
  taskId: string;
  threadId: string;
  /** The account the task lives under — the door needs it, the live doc carries it. */
  accountId: string;
  askerUid: string;
}

interface PublishedLiveTurns {
  docs: ReadonlyArray<LiveTurnView>;
  publishedAtMs: number;
}

const byThread = new Map<string, PublishedLiveTurns>();

/** The host publishes the open thread's live docs (every snapshot, the empty one included). */
export function publishThreadLiveTurns(
  threadId: string | null | undefined,
  docs: ReadonlyArray<LiveTurnView> | null | undefined,
  nowMs: number = Date.now(),
): void {
  const id = (threadId || '').trim();
  if (!id) return;
  byThread.set(id, { docs: Array.isArray(docs) ? docs : [], publishedAtMs: nowMs });
}

/** The host leaves the thread (or unmounts): nothing on it is a target any more. */
export function clearThreadLiveTurns(threadId: string | null | undefined): void {
  const id = (threadId || '').trim();
  if (!id) return;
  byThread.delete(id);
}

export interface ReadParticipantAttachTargetOptions {
  nowMs?: number;
  /** This tab's own live sends (the liveSendRegistry by default). */
  isOwnTask?: (taskId: string) => boolean;
  staleMs?: number;
  ttlMs?: number;
}

/**
 * The turn a participant's send should attach to, or null when the send takes
 * today's path. Deterministic: the newest by `startedAtMs`, ties by task id.
 */
export function readParticipantAttachTarget(
  threadId: string | null | undefined,
  viewerUid: string | null | undefined,
  options: ReadParticipantAttachTargetOptions = {},
): ParticipantAttachTarget | null {
  const id = (threadId || '').trim();
  const viewer = (viewerUid || '').trim();
  if (!id || !viewer) return null;
  const entry = byThread.get(id);
  if (!entry) return null;
  const nowMs = options.nowMs ?? Date.now();
  const ttlMs = options.ttlMs ?? LIVE_TURN_ADMISSION_TTL_MS;
  if (nowMs - entry.publishedAtMs > ttlMs) {
    byThread.delete(id);
    return null;
  }
  const isOwnTask = options.isOwnTask ?? isLiveSendInFlight;
  const staleMs = options.staleMs ?? LIVE_TURN_STALE_MS;
  // This tab drives a turn on the thread: the own interjection path owns the
  // send (a note to one's own running work), never the participant door.
  for (const d of entry.docs) {
    if (d && d.taskId && isOwnTask(d.taskId)) return null;
  }
  let best: LiveTurnView | null = null;
  for (const d of entry.docs) {
    if (!d || !d.taskId || !d.accountId) continue;
    if (d.noteAdmission !== 'participants') continue;
    if (!d.askerUid || d.askerUid === viewer) continue;
    if (nowMs - d.updatedAtMs > staleMs) continue;
    if (
      !best
      || d.startedAtMs > best.startedAtMs
      || (d.startedAtMs === best.startedAtMs && d.taskId > best.taskId)
    ) {
      best = d;
    }
  }
  if (!best) return null;
  return { taskId: best.taskId, threadId: best.threadId || id, accountId: best.accountId, askerUid: best.askerUid };
}

export function __resetLiveTurnAdmissionRegistryForTests(): void {
  byThread.clear();
}
